import * as Sentry from '@sentry/angular';
import { isDemoMode } from './core.demo';
import { DestroyRef, inject, provideAppInitializer } from '@angular/core';
import { DBConfig, NgxIndexedDBService, provideIndexedDb } from 'ngx-indexed-db';
import { delay, filter, switchMap, tap } from 'rxjs/operators';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { appReload, ClusterService, WatchdogService } from './index';
import { isTruthy } from './lib/utils';
import { timer } from 'rxjs';

export const databaseNamePrepare = (name: string): string => {
  if (isDemoMode) {
    return `${ name }.demo`;
  }

  return name;
};

export const coreDatabase: DBConfig = {
  name: databaseNamePrepare('excuse-me.offline'),
  isDefault: true as const,
  version: 8,
  objectStoresMeta: [
    {
      store: 'keyValue',
      storeConfig: { keyPath: 'key', autoIncrement: false },
      storeSchema: [
        { name: 'key', keypath: 'key', options: { unique: true } },
        { name: 'value', keypath: 'value', options: { unique: false } },
      ],
    },
    {
      store: 'fileCache',
      storeConfig: { keyPath: 'url', autoIncrement: false },
      storeSchema: [
        { name: 'url', keypath: 'url', options: { unique: true } },
        { name: 'blob', keypath: 'blob', options: { unique: false } },
        { name: 'createdAt', keypath: 'createdAt', options: { unique: false } },
      ],
    },
    {
      store: 'menus',
      storeConfig: { keyPath: 'id', autoIncrement: true },
      storeSchema: [
        { name: 'createdAt', keypath: 'createdAt', options: { unique: false } },
        { name: 'file', keypath: 'file', options: { unique: false } },
        { name: 'happyHours', keypath: 'happyHours', options: { unique: false } },
        { name: 'hidden', keypath: 'hidden', options: { unique: false } },
        { name: 'icon', keypath: 'icon', options: { unique: false } },
        { name: 'banner', keypath: 'banner', options: {unique: false} },
        { name: 'name', keypath: 'name', options: { unique: false } },
        { name: 'ownerType', keypath: 'ownerType', options: { unique: false } },
        { name: 'pages', keypath: 'pages', options: { unique: false } },
      ]
    },
    {
      store: 'happyHoursPeriod',
      storeConfig: { keyPath: 'id', autoIncrement: false },
      storeSchema: [
        { name: 'id', keypath: 'id', options: { unique: true } },
        { name: 'startDay', keypath: 'startDay', options: { unique: false } },
        { name: 'startTime', keypath: 'startTime', options: { unique: false } },
        { name: 'endDay', keypath: 'endDay', options: { unique: false } },
        { name: 'endTime', keypath: 'endTime', options: { unique: false } },
      ],
    },
    {
      store: 'specials',
      storeConfig: { keyPath: 'id', autoIncrement: false },
      storeSchema: [
        { name: 'id', keypath: 'id', options: { unique: true } },
        { name: 'ownerType', keypath: 'ownerType', options: { unique: false } },
        { name: 'name', keypath: 'name', options: { unique: false } },
        { name: 'mimeType', keypath: 'mimeType', options: { unique: false } },
        { name: 'scheduleStart', keypath: 'scheduleStart', options: { unique: false } },
        { name: 'scheduleEnd', keypath: 'scheduleEnd', options: { unique: false } },
        { name: 'sequence', keypath: 'sequence', options: { unique: false } },
        { name: 'content', keypath: 'content', options: { unique: false } },
        { name: 'updatedAt', keypath: 'updatedAt', options: { unique: false } },
        { name: 'createdAt', keypath: 'createdAt', options: { unique: false } },
      ],
    },
    {
      store: 'userActivity',
      storeConfig: { keyPath: 'id', autoIncrement: true },
      storeSchema: [
        { name: 'id', keypath: 'id', options: { unique: true } },
        { name: 'app', keypath: 'app', options: { unique: false } },
        { name: 'appVersion', keypath: 'appVersion', options: { unique: false } },
        { name: 'appId', keypath: 'appId', options: { unique: false } },
        { name: 'mainApp', keypath: 'mainApp', options: { unique: false } },
        { name: 'environment', keypath: 'environment', options: { unique: false } },
        { name: 'action', keypath: 'action', options: { unique: false } },
        { name: 'target', keypath: 'target', options: { unique: false } },
        { name: 'screen', keypath: 'screen', options: { unique: false } },
        { name: 'section', keypath: 'section', options: { unique: false } },
        { name: 'clientX', keypath: 'clientX', options: { unique: false } },
        { name: 'clientY', keypath: 'clientY', options: { unique: false } },
        { name: 'capturedAt', keypath: 'capturedAt', options: { unique: false } },
        { name: 'sentAt', keypath: 'sentAt', options: { unique: false } },
      ],
    },
  ],
  migrationFactory: () => {
    return {
      3: (db: IDBDatabase) => {
        if (db.objectStoreNames.contains('happyHours')) {
          db.deleteObjectStore('happyHours');
        }
      },
    };
  },
};

export const provideCoreDatabase = (configs: DBConfig | DBConfig[] = []) => {
  return [
    provideIndexedDb(...[
      coreDatabase,
      ...(
        Array.isArray(configs) ? configs : [configs]
      ),
    ]),
    provideAppInitializer(() => {
      const logger = inject(WatchdogService).tag('Database', 'red');
      const destroyRef = inject(DestroyRef);
      const cluster = inject(ClusterService);
      const db = inject(NgxIndexedDBService);

      // Check if the database is empty and delete it if it is
      cluster.leader$.pipe(
        filter(isTruthy),
        // Wait for 3 seconds before starting the check and then every 12 minutes
        switchMap(() => timer(3_000, 720_000)),
        tap(() => {
          logger.info('Checking database health...');
        }),
        switchMap(() => db.getAllObjectStoreNames()),
        tap((names) => {
          if (names.length === 0) {
            logger.error('Database broken, deleting...');
            Sentry.captureMessage('Database broken!', 'fatal');
          }
          else {
            logger.info('Database is healthy:', names.join(', '));
          }
        }),
        // If the database is empty, delete it
        filter((names) => names.length === 0),
        switchMap(() => db.deleteDatabase()),
        tap(() => {
          logger.info('Database deleted, reloading sidecar apps...');
          void cluster.broadcast('reload');
        }),
        // Wait for 2 seconds before reloading leader app
        delay(2_000),
        takeUntilDestroyed(destroyRef),
      ).subscribe(() => {
        logger.info('Reloading leader app...');
        appReload();
      });
    }),
  ];
};
